import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, Check, ChevronRight, CheckCircle2 } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { CONTACT } from "./marketingData";
import { breadcrumb, faqSchema, SITE } from "./marketingData";
import { guideBySlug } from "./seoContent";
import { CITIES } from "./seoContent";

export default function GuidePage() {
  const { slug } = useParams();
  const guide = guideBySlug(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!guide) return <Navigate to="/" replace />;

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Article",
      headline: guide.title, image: guide.image, datePublished: "2026-06-01", dateModified: "2026-06-01",
      author: { "@type": "Organization", name: "Indian Transport Organisation" },
      publisher: { "@type": "Organization", name: "Indian Transport Organisation", logo: { "@type": "ImageObject", url: `${SITE}/ito-logo.png` } },
      mainEntityOfPage: `${SITE}/guides/${guide.slug}`,
    },
    breadcrumb([{ name: "Home", path: "/" }, { name: "Guides", path: "/guides/" + guide.slug }]),
    faqSchema(guide.faqs),
  ];

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden" data-testid={`guide-page-${guide.slug}`}>
      <Seo
        title={guide.metaTitle}
        description={guide.metaDescription}
        path={`/guides/${guide.slug}`}
        image={guide.image}
        imageAlt={guide.title}
        type="article"
        keywords={guide.keywords}
        jsonLd={jsonLd}
      />
      <MarketingNav heroDark />

      {/* Hero */}
      <section className="relative min-h-[56vh] flex items-end pb-14 pt-32 overflow-hidden">
        <img src={guide.image} alt={guide.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/93 via-[#1A1A18]/60 to-[#1A1A18]/30" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 w-full">
          <div className="flex items-center gap-2 text-xs text-white/80 mb-5">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#D4A373]">Guide</span>
          </div>
          <h1 className="font-heading font-black tracking-tight text-white text-3xl sm:text-5xl leading-[1.05] max-w-3xl">{guide.title}</h1>
          <p className="mt-2 text-white/70 text-sm">Updated {guide.updated} · Indian Transport Organisation</p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-lg text-[#4F4C44] leading-relaxed">{guide.intro}</p>
          </Reveal>

          {guide.sections.map((s) => (
            <Reveal key={s.h} className="mt-12">
              <h2 className="font-heading font-bold text-2xl mb-4">{s.h}</h2>
              {s.body && <p className="text-base text-[#4F4C44] leading-relaxed">{s.body}</p>}
              {s.bullets && (
                <ul className="mt-4 space-y-3">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[#1A1A18]">
                      <CheckCircle2 className="w-5 h-5 text-[#C84B31] shrink-0 mt-0.5" />
                      <span className="text-base leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}

          {/* Comparison table */}
          {guide.comparison && (
            <Reveal className="mt-12">
              <h2 className="font-heading font-bold text-2xl mb-5">{guide.comparison.title}</h2>
              <div className="overflow-hidden rounded-xl border border-[#1A1A18]/10">
                <table className="w-full text-sm" data-testid="guide-comparison-table">
                  <thead>
                    <tr className="bg-[#1A1A18] text-white text-left">
                      <th className="px-4 py-3 font-semibold"> </th>
                      <th className="px-4 py-3 font-semibold">{guide.comparison.colA}</th>
                      <th className="px-4 py-3 font-semibold">{guide.comparison.colB}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1A1A18]/10">
                    {guide.comparison.rows.map((r) => (
                      <tr key={r.f} className="bg-white">
                        <td className="px-4 py-3 font-semibold text-[#1A1A18]">{r.f}</td>
                        <td className="px-4 py-3 text-[#4F4C44]">{r.a}</td>
                        <td className="px-4 py-3 text-[#4F4C44]">{r.b}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
          )}

          {/* CTA */}
          <Reveal className="mt-14">
            <div className="rounded-2xl bg-[#1A1A18] text-white p-8">
              <h2 className="font-heading font-bold text-2xl mb-2">Need a {guide.ctaVehicle}?</h2>
              <p className="text-white/80 text-sm leading-relaxed mb-6">Tell us your cargo, route and dates — we'll quote your {guide.ctaVehicle} movement to any pincode in India.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={CONTACT.phoneHref} data-testid="guide-quote-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
                  <Phone className="w-4 h-4" /> Call {CONTACT.phone}
                </a>
                {guide.fleetSlug && (
                  <Link to={`/fleet/${guide.fleetSlug}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-[#1A1A18] transition-colors">
                    See the vehicle <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection faqs={guide.faqs} title={`${guide.ctaVehicle} transport — FAQ`} testid="guide-faq" />

      {/* City links for internal linking */}
      <section className="py-14 bg-white border-t border-[#1A1A18]/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading font-bold text-2xl mb-2">{guide.ctaVehicle} transport by city</h2>
          <p className="text-sm text-[#4F4C44] mb-6">We run {guide.ctaVehicle} movements to and from every major industrial hub in India.</p>
          <div className="flex flex-wrap gap-2.5">
            {CITIES.map((c) => (
              <Link key={c.slug} to={`${guide.cityBase}/${c.slug}`} data-testid={`guide-city-${c.slug}`}
                className="rounded-full border border-[#1A1A18]/15 px-3.5 py-1.5 text-sm text-[#1A1A18] hover:border-[#C84B31] hover:text-[#C84B31] transition-colors">
                {c.name}
              </Link>
            ))}
          </div>
          <Link to="/fleet" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">
            <ArrowLeft className="w-4 h-4" /> Explore our full fleet
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
